import { Button } from '@edsn/pie-components';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCurrentOrganisation } from '#pie/auth';
import {
  type CreateContactRequest,
  useGetContactGroups,
  useGetManagementContacts,
} from '#pie/internal/api/pie-bff';
import { typedFormFields } from '#pie/utils/typedFormFields';
import type { Option } from '../input-dropdown/InputDropdown';
import type { MultiDropdownOption } from '../input-multi-dropdown/InputMultiDropdown';
import { Stack } from '../stack/Stack';

export type ContactFormValues = Omit<CreateContactRequest, 'contactGroups'> & {
  contactGroups: string[];
  organisationId?: string;
};

const { FormText, FormDropdown, FormMultiDropdown, FormPhone } =
  typedFormFields<ContactFormValues>();

export interface ContactFormProps {
  onSubmit: SubmitHandler<CreateContactRequest>;
  isLoading?: boolean;
  submitText: string;
  step: number;
  defaultValues?: Partial<ContactFormValues>;
  onBack?: () => void;
  onNext?: () => void;
  onFlowBack?: () => void;
}

export const ContactForm = ({
  onSubmit,
  isLoading,
  submitText,
  onNext,
  onBack,
  step = 1,
  defaultValues,
  onFlowBack,
}: ContactFormProps) => {
  const { t } = useTranslation();
  const { data: contactGroups } = useGetContactGroups();
  const { data: contacts } = useGetManagementContacts({
    pagination: { pageIndex: 0, pageSize: 10000 },
    filter: {},
  });
  const { currentAccount } = useCurrentOrganisation();
  const subOrganisations = currentAccount?.subOrganisations;
  const showAdministrativeDropdown =
    subOrganisations && subOrganisations.length > 1;
  const firstOrganisationId = subOrganisations?.[0]?.id;

  const contactGroupOptions: MultiDropdownOption[] =
    contactGroups
      ?.filter(
        (item) =>
          currentAccount?.marketRole &&
          item.marketRoles.includes(currentAccount.marketRole),
      )
      .map((item) => ({
        label: item.name,
        value: item.id,
      })) ?? [];

  const subOrganisationsOptions: Option[] =
    subOrganisations?.map((item) => ({
      label: item.label,
      value: item.id,
    })) ?? [];

  const formMethods = useForm<ContactFormValues>({
    shouldUnregister: false,
    defaultValues: {
      contactGroups: defaultValues?.contactGroups ?? [],
      organisationId: defaultValues?.organisationId,
      firstName: defaultValues?.firstName ?? '',
      lastName: defaultValues?.lastName ?? '',
      email: defaultValues?.email ?? '',
      phone: defaultValues?.phone ?? '',
    },
  });

  const { clearErrors, getValues, handleSubmit, setError, trigger } =
    formMethods;

  // this should be if (valid) onNext() but update contact modal is also using this for now

  const handleNext = async () => {
    const valid = await trigger(['firstName', 'lastName', 'email', 'phone']);

    if (!valid) {
      return;
    }

    const email = getValues('email');
    const emailExists = contacts?.items.some(
      (contact) => contact.email?.toLowerCase() === email.toLowerCase(),
    );

    if (emailExists) {
      setError('email', {
        type: 'validate',
        message: t('cpr_contact_group_detail.create_user.error.email_exists'),
      });
      return;
    }

    clearErrors('email');

    if (onNext) {
      onNext();
    }
  };

  const handleFormSubmit = handleSubmit(({ organisationId, ...data }) => {
    onSubmit({
      ...data,
      contactGroups: data.contactGroups.map((contactGroupId) => ({
        contactGroupId,
        organisationId: organisationId || firstOrganisationId,
      })),
    });
  });

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleFormSubmit}>
        <Stack gap="lg">
          <Stack gap="sm" className={step !== 1 ? 'hidden' : ''}>
            <FormText
              fullWidth
              name="firstName"
              label={t('common.first_name')}
              rules={{ required: true, maxLength: 16 }}
              placeholder={t('common.placeholder_first_name')}
            />
            <FormText
              fullWidth
              name="lastName"
              label={t('common.last_name')}
              rules={{ required: true }}
              placeholder={t('common.placeholder_last_name')}
            />
            <FormText
              fullWidth
              name="email"
              type="email"
              label={t('common.email')}
              rules={{
                required: true,
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t('common.errors.invalid_email'),
                },
              }}
              placeholder={t('common.placeholder_email')}
            />
            <FormPhone name="phone" rules={{ required: true }} />
          </Stack>
          {step === 1 && (
            <div className="flex flex-row gap-4">
              {onFlowBack && (
                <Button
                  type="button"
                  action="tertiary"
                  emphasis="subtle"
                  className="w-full"
                  onClick={onFlowBack}
                >
                  {t('cpr_management_group_detail.button.back')}
                </Button>
              )}

              <Button
                type="button"
                action="secondary"
                emphasis="bold"
                onClick={handleNext}
                className="w-full"
              >
                {t('common.steps.next')}
              </Button>
            </div>
          )}
          <Stack gap="sm" className={step !== 2 ? 'hidden' : ''}>
            {showAdministrativeDropdown && (
              <FormDropdown
                options={subOrganisationsOptions}
                label={t('common.subunit')}
                name="organisationId"
                fullWidth
                isLoading={isLoading}
                rules={{ required: true }}
                placeholder={t(
                  'cpr_contact_group_create_contact.subunit.placeholder',
                )}
              />
            )}
            <FormMultiDropdown
              options={contactGroupOptions}
              label={t('common.contact_group')}
              name="contactGroups"
              fullWidth
              isLoading={isLoading}
              rules={{ required: true }}
              placeholder={t(
                'cpr_contact_group_create_contact.contact_group.placeholder',
              )}
            />
          </Stack>
          {step === 2 && (
            <div className="flex flex-row gap-4">
              <Button
                type="button"
                action="tertiary"
                emphasis="subtle"
                className="w-full"
                onClick={onBack}
              >
                {t('cpr_management_group_detail.button.back')}
              </Button>

              <Button isLoading={isLoading} type="submit" className="w-full">
                {submitText}
              </Button>
            </div>
          )}
        </Stack>
      </form>
    </FormProvider>
  );
};
